/**
 * Adapt tournament-standings API groups to `SportStandingsTable` rows
 * (specs/champions-league, Story 5).
 *
 * The API and the table disagree on two field names (`goalDifference` vs `goalDiff`) and the
 * API row carries no stable key. Both are fixed here rather than in either component, so the
 * table stays sport-generic and the payload stays provider-shaped.
 *
 * Pure — no React.
 */

import type {
  WorldCupGroupStageTable,
  WorldCupNationalTeamStandingRow,
} from '../../worldcup/types/worldCupTournamentStandings.types';

export interface TournamentStandingsRow {
  teamId: string;
  teamName: string;
  position: number;
  kit?: { primary?: string; secondary?: string; type?: string } | null;
  /** Index signature so this satisfies the table's open row shape. */
  [key: string]: unknown;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
}

function toRow(row: WorldCupNationalTeamStandingRow, index: number): TournamentStandingsRow {
  return {
    // Fall back to the name only when the server could not resolve the club: a duplicate key
    // is still better than a missing one, and the row is already degraded at that point.
    teamId: row.teamId ?? `unresolved-${row.name}-${index}`,
    teamName: row.name,
    position: row.position ?? index + 1,
    // Null kit is deliberate: TeamCrest draws its own default, and the caller may substitute
    // a name-based lookup. Never fabricate colours here. `type` is widened to
    // `string | undefined` because the table's row shape does not model an explicit null.
    kit: row.kit
      ? { primary: row.kit.primary, secondary: row.kit.secondary, type: row.kit.type ?? undefined }
      : null,
    played: row.played,
    won: row.won,
    drawn: row.drawn,
    lost: row.lost,
    goalsFor: row.goalsFor,
    goalsAgainst: row.goalsAgainst,
    // The table's football column set reads `goalDiff`; the API sends `goalDifference`.
    goalDiff: row.goalDifference,
    points: row.points,
  };
}

/**
 * Flatten the API's groups into table rows. A league-phase competition sends exactly one
 * group, so this is normally a single table; a grouped tournament keeps its groups.
 */
export function buildStandingsRows(
  group: WorldCupGroupStageTable | null | undefined,
): TournamentStandingsRow[] {
  if (!group?.teams?.length) return [];
  return group.teams.map(toRow);
}

/**
 * Heading for a table. A lettered group reads "Group C"; a single-table league phase has no
 * letter and needs no heading at all, since the screen title already names the competition.
 */
export function standingsTableHeading(group: WorldCupGroupStageTable): string | null {
  return group.groupLetter ? `Group ${group.groupLetter}` : null;
}
